import React, { useEffect, useState } from "react";
import PageHeader from "../../core/common/header/PageHeader";
import {
  FaUpload,
  FaCheckCircle,
  FaTimesCircle,
  FaFileAlt,
} from "react-icons/fa";

interface Course {
  id: number;
  title: string;
  hasTemplate: boolean;
}

const CourseRegulationTemplates: React.FC = () => {
  const API = process.env.REACT_APP_API_BASE_URL;

  const [courses, setCourses] = useState<Course[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState<number | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const [lastUpdated, setLastUpdated] = useState<Date>(new Date());
  const [successCourse, setSuccessCourse] = useState<string | null>(null);

  useEffect(() => {
    loadCourses();
  }, []);

  const loadCourses = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API}/api/courses`, {
        credentials: "include",
      });
      const data = await response.json();

      const coursesWithStatus = await Promise.all(
        data.map(async (course: any) => {
          const hasTemplateRes = await fetch(
            `${API}/api/admin/courses/${course.id}/has-regulation-template`,
            { credentials: "include" }
          );
          const hasTemplate = await hasTemplateRes.json();

          return {
            id: course.id,
            title: course.title,
            hasTemplate,
          };
        })
      );

      setCourses(coursesWithStatus);
      setLastUpdated(new Date());
    } catch (err) {
      console.error("Failed to load courses", err);
      alert("Failed to load courses");
    } finally {
      setLoading(false);
    }
  };

  const refreshData = async () => {
    setRefreshing(true);
    await loadCourses();
    setRefreshing(false);
  };

  const handleUploadTemplate = async (courseId: number, file: File) => {
    if (!file) return;

    if (file.type !== "application/pdf") {
      alert("Please upload a PDF file only");
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      alert("File size must be less than 10MB");
      return;
    }

    try {
      setUploading(courseId);
      const formData = new FormData();
      formData.append("file", file);

      const response = await fetch(
        `${API}/api/admin/courses/${courseId}/regulation-template`,
        {
          method: "POST",
          credentials: "include",
          body: formData,
        }
      );

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || "Upload failed");
      }

      const courseTitle = courses.find((c) => c.id === courseId)?.title ?? "";
      await loadCourses();
      setSuccessCourse(courseTitle);
    } catch (err: any) {
      alert(err.message || "Failed to upload template");
      console.error(err);
    } finally {
      setUploading(null);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#009fe3] mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading courses...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Success Modal */}
      {successCourse && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-sm w-full mx-4 text-center">
            <div className="flex items-center justify-center w-16 h-16 bg-emerald-100 rounded-full mx-auto mb-4">
              <FaCheckCircle className="w-8 h-8 text-emerald-500" />
            </div>
            <h2 className="text-xl font-bold text-gray-800 mb-2">
              Template Uploaded
            </h2>
            <p className="text-gray-500 mb-6">
              Regulation template for{" "}
              <span className="font-semibold text-gray-700">{successCourse}</span>{" "}
              has been saved successfully.
            </p>
            <button
              onClick={() => setSuccessCourse(null)}
              className="w-full py-2.5 bg-[#009fe3] text-white font-semibold rounded-xl hover:bg-cyan-600 transition-colors"
            >
              Done
            </button>
          </div>
        </div>
      )}

      {/* Header */}
      <PageHeader
        icon={<FaFileAlt />}
        title="Regulation Templates"
        breadcrumb={[
          { label: "Home", to: "/" },
          { label: "Dashboard", to: "/admin/dashboard" },
          { label: "Regulations", to: "/admin/regulation-review" },
          { label: "Templates" },
        ]}
      />

      {/* Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-6 sm:py-10">
        <div className="mb-6 bg-blue-50 border border-blue-200 rounded-xl p-6 shadow-sm">
          <h3 className="text-lg font-semibold text-blue-900 mb-2">
            Instructions
          </h3>
          <p className="text-blue-800">
            Upload a PDF regulation template for each course. Students will
            download this template, sign it, and upload it back for approval.
          </p>
        </div>

        {courses.length === 0 ? (
          <div className="bg-white rounded-xl shadow-lg p-8 text-center">
            <p className="text-gray-600">No courses found.</p>
          </div>
        ) : (
          <div className="grid gap-4">
            {courses.map((course) => (
              <div
                key={course.id}
                className="bg-white rounded-xl shadow-lg p-6 flex items-center justify-between hover:shadow-xl transition-shadow"
              >
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-gray-800">
                    {course.title}
                  </h3>
                  <div className="mt-2">
                    {course.hasTemplate ? (
                      <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-emerald-50 text-emerald-700 rounded-full text-xs font-semibold border border-emerald-200">
                        <FaCheckCircle className="w-3.5 h-3.5" />
                        Template Uploaded
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-rose-50 text-rose-600 rounded-full text-xs font-semibold border border-rose-200">
                        <FaTimesCircle className="w-3.5 h-3.5" />
                        No Template
                      </span>
                    )}
                  </div>
                </div>

                <label
                  className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg font-semibold transition-all shadow-sm ${
                    uploading === course.id
                      ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                      : "bg-[#009fe3] text-white hover:bg-cyan-600 hover:shadow-md cursor-pointer"
                  }`}
                >
                  <FaUpload className="w-4 h-4" />
                  {uploading === course.id
                    ? "Uploading..."
                    : course.hasTemplate
                    ? "Replace Template"
                    : "Upload Template"}
                  <input
                    type="file"
                    accept=".pdf"
                    className="hidden"
                    disabled={uploading === course.id}
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file && uploading === null) {
                        handleUploadTemplate(course.id, file);
                      }
                      e.target.value = "";
                    }}
                  />
                </label>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
};

export default CourseRegulationTemplates;
